/* eslint-disable deprecation/deprecation */
import * as React from 'react';
import { PopupStyle } from '../styles/Standard';
import { GlobalPointerDownOutsideListener } from '../utils/GlobalPointerDownOutsideListener';
import { HorizontalOriginOption, Overlay, OverlayProps, VerticalOriginOption } from '../utils/Overlay';
// eslint-disable-next-line import/no-cycle
import { GenericListMenuProps, ListMenuWidget } from './ListMenuWidget';

export interface BaseFlyoutListMenuProps extends Omit<OverlayProps, 'autoFocus' | 'disablePointerEvents' | 'portalEventTrapOverrides'> {
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  /**
   * The menu will not be re-triggered by clicking this element when the menu is already open. The menu will attempt to re-focus this element when it closes.
   */
  toggleEl?: HTMLElement | null;
  /**
   * @deprecated As of v9.32.0, this behavior is no longer configurable. Focus will always be returned to `toggleEl` when the menu closes.
   */
  shouldReturnFocusToToggleEl?: boolean;
}

/** @deprecated unsupported API, instead use `FlyoutListMenuWidget` */
export interface GenericFlyoutListMenuProps extends BaseFlyoutListMenuProps, GenericListMenuProps {
}

const DefaultAnchorOrigin = {
  vertical: VerticalOriginOption.bottom,
  horizontal: HorizontalOriginOption.left
};

/** @deprecated unsupported API, instead use `FlyoutListMenuWidget` */
export class GenericFlyoutListMenuWidget extends React.Component<GenericFlyoutListMenuProps> {
  private unsubscribeFlyoutFromGlobalListener: (() => void) | null = null;
  private unsubscribeToggleElFromGlobalListener: (() => void) | null = null;
  private flyoutDomNode: HTMLElement | null = null;

  public static defaultProps: Pick<GenericFlyoutListMenuProps, 'anchorOrigin' | 'role'> = {
    anchorOrigin: DefaultAnchorOrigin,
    role: 'menu',
  };

  public componentDidMount(): void {
    // Everything outside the toggling element/button or the flyout should trigger a close on mousedown
    if (this.flyoutDomNode) {
      this.unsubscribeFlyoutFromGlobalListener = GlobalPointerDownOutsideListener.subscribe(
        this.flyoutDomNode,
        this.props.requestCloseAll
      );
    }
    if (this.props.toggleEl) {
      this.unsubscribeToggleElFromGlobalListener = GlobalPointerDownOutsideListener.subscribe(
        this.props.toggleEl,
        this.props.requestCloseAll
      );
    }
  }

  public componentWillUnmount(): void {
    this.unsubscribeFlyoutFromGlobalListener?.();
    this.unsubscribeToggleElFromGlobalListener?.();

    this.props.toggleEl?.focus();
  }

  private setFlyoutDomNode = (ref: HTMLDivElement | null): void => {
    this.flyoutDomNode = ref;
  }

  private getAnchorClientRect = (): ClientRect | undefined => {
    if (this.props.getAnchorClientRect) {
      return this.props.getAnchorClientRect();
    }

    return this.props.anchorEl ? this.props.anchorEl.getBoundingClientRect() : undefined;
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = e => {
    if (e.key === 'Tab') {
      e.stopPropagation();
      e.preventDefault();
      this.props.requestCloseAll();
    }
  }

  public render(): JSX.Element {
    // eslint-disable-next-line max-len
    const { allowAutoPosition, anchorOrigin, handleScrollOnScrollingRoot, menuStyle, onMouseEnter, scrollingRootId, shouldAutoPositionOnScrollingRootScroll, stackedOrigin, stackingRootId, zIndex, ...menuProps } = this.props;

    const overlayProps: OverlayProps = {
      allowAutoPosition,
      anchorOrigin,
      getAnchorClientRect: this.getAnchorClientRect,
      handleScrollOnScrollingRoot,
      scrollingRootId,
      shouldAutoPositionOnScrollingRootScroll,
      stackedOrigin,
      stackingRootId,
      zIndex,
    };

    const containerProps: React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> = {
      onKeyDown: this.handleKeyDown, // not using FocusTrap for this because it'll prevent submenu navigation
      onMouseEnter,
      ref: this.setFlyoutDomNode,
      style: { ...PopupStyle, ...menuStyle },
    };

    const listMenuProps: GenericListMenuProps = {
      ...menuProps,
      scrollingRootId: overlayProps.scrollingRootId,
      stackingRootId: overlayProps.stackingRootId,
      subMenuAllowAutoPosition: overlayProps.allowAutoPosition,
      subMenuShouldAutoPositionOnScrollingRootScroll: overlayProps.shouldAutoPositionOnScrollingRootScroll,
      zIndex: overlayProps.zIndex,
    };

    return (
      <Overlay {...overlayProps}>
        <div {...containerProps}>
          <ListMenuWidget {...listMenuProps} />
        </div>
      </Overlay>
    );
  }
}
